import { useCallback, useState } from 'react';
import { Locale, LOCALES } from './types';

const STORAGE_KEY = 'deeplabeling.docs.locale';

/** The browser's language when it is one the documentation is written in, English otherwise. */
export function defaultLocale(
  languages: readonly string[] = navigator.languages ?? [navigator.language]
): Locale {
  for (const language of languages) {
    const code = language?.slice(0, 2).toLowerCase();
    if ((LOCALES as string[]).includes(code)) {
      return code as Locale;
    }
  }
  return 'en';
}

function storedLocale(): Locale | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return (LOCALES as string[]).includes(stored) ? (stored as Locale) : null;
  } catch {
    return null; // storage blocked: the browser's language, every time
  }
}

/** The documentation's language: the reader's last choice on this browser, else the browser's language. */
export function useDocsLocale(): [Locale, (locale: Locale) => void] {
  const [locale, setLocale] = useState<Locale>(
    () => storedLocale() ?? defaultLocale()
  );
  const choose = useCallback((next: Locale) => {
    setLocale(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // kept for this visit only
    }
  }, []);
  return [locale, choose];
}

/** The documentation page's own words. */
export const UI: Record<
  Locale,
  {
    title: string;
    userGuide: string;
    adminGuide: string;
    guides: string;
    searchLabel: string;
    searchPlaceholder: string;
    noResults: (query: string) => string;
    results: (count: number) => string;
    contents: string;
    previous: string;
    next: string;
    notFound: string;
    language: string;
  }
> = {
  en: {
    title: 'Documentation',
    userGuide: 'User guide',
    adminGuide: 'Data manager guide',
    guides: 'Guide',
    searchLabel: 'Search the documentation',
    searchPlaceholder: 'Search, e.g. validate, shortcuts, review…',
    noResults: query => `Nothing matches “${query}”.`,
    results: count => (count === 1 ? '1 result' : `${count} results`),
    contents: 'On this page',
    previous: 'Previous',
    next: 'Next',
    notFound: 'This page does not exist, or is not part of your guide.',
    language: 'Language',
  },
  fr: {
    title: 'Documentation',
    userGuide: 'Guide utilisateur',
    adminGuide: 'Guide data manager',
    guides: 'Guide',
    searchLabel: 'Rechercher dans la documentation',
    searchPlaceholder: 'Rechercher, p. ex. valider, raccourcis, review…',
    noResults: query => `Aucun résultat pour « ${query} ».`,
    results: count => (count <= 1 ? `${count} résultat` : `${count} résultats`),
    contents: 'Sur cette page',
    previous: 'Précédent',
    next: 'Suivant',
    notFound: "Cette page n'existe pas, ou ne fait pas partie de votre guide.",
    language: 'Langue',
  },
};
